import type { DailyRow, FunnelDetail, FunnelSummary, FunnelTotals, PageDetailData, PageLeads, PageSummary, VariantComparison, VariantStats } from "./api";

/** Below this many new visitors per version, a comparison is not stated. */
export const MIN_VISITORS_TO_COMPARE = 100;

const COUNT = new Intl.NumberFormat("en-GB");

export function formatCount(value: number): string {
  return COUNT.format(value);
}

/** A rate the Worker could not compute (no new visitors) is a dash, never 0%. */
export function formatPct(value: number | null): string {
  return value === null ? "–" : `${value.toFixed(1)}%`;
}

export function variantLabel(variant: string): string {
  return `Version ${variant.toUpperCase()}`;
}

/**
 * The split-test verdict. It states the Worker's rates side by side and
 * nothing more: it never names a winner and never computes significance,
 * because the portal does not recompute figures (I-4).
 */
export function verdictLine(detail: Pick<FunnelDetail, "splitting" | "variants">): string {
  if (!detail.splitting) return "Only one version is live, so there is nothing to compare.";
  if (detail.variants.some((v) => v.new_visitors < MIN_VISITORS_TO_COMPARE)) {
    return `Too early to call: each version needs at least ${MIN_VISITORS_TO_COMPARE} visitors.`;
  }
  const [first, ...rest] = [...detail.variants].sort((a, b) => a.variant.localeCompare(b.variant));
  if (rest.length === 1) {
    const other = rest[0];
    return `Version ${first.variant.toUpperCase()} converts at ${formatPct(first.conversion_pct)} against ${formatPct(other.conversion_pct)} for version ${other.variant.toUpperCase()}.`;
  }
  const parts = [first, ...rest].map((v) => `version ${v.variant.toUpperCase()} at ${formatPct(v.conversion_pct)}`);
  return `Conversion: ${parts.join(", ")}.`;
}

/** `YYYY-MM-DD` of the first day inside a `days`-long window ending today (UTC). */
export function windowStart(days: number, today: Date = new Date()): string {
  const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  start.setUTCDate(start.getUTCDate() - (days - 1));
  return start.toISOString().slice(0, 10);
}

/** The earliest `counting_since` that falls inside the window, or `null`. */
export function countingStartedInWindow(
  funnels: Pick<FunnelSummary, "counting_since">[],
  days: number,
  today: Date = new Date(),
): string | null {
  const start = windowStart(days, today);
  const inside = funnels
    .map((f) => f.counting_since)
    .filter((d): d is string => d !== null && d > start)
    .sort();
  return inside[0] ?? null;
}

export function formatDay(day: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return day;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

/** Sign-ups shown in the Overview: every sign-up in the window, beside Reached Drip. */
export function signupsInWindow(funnel: Pick<FunnelSummary, "signups" | "signups_in_window">): number {
  return funnel.signups_in_window ?? funnel.signups;
}

export function needsAttention(funnel: Pick<FunnelSummary, "handover_pending" | "handover_failed">): number {
  return funnel.handover_pending + funnel.handover_failed;
}

export type ChartRow = { day: string } & Record<string, number | string | undefined>;

/** Days from `from` to `to` inclusive, as `YYYY-MM-DD` (UTC). */
function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  const cursor = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime())) return out;
  while (cursor <= end) {
    out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

export type ChartFill = { from: string | null; to: string; variants: string[] };

/**
 * One chart row per day, keyed `visitors_<v>` and `signups_<v>`. Once counting
 * has started (`fill.from`), a day a variant has no row for is a real zero, so
 * it is filled with 0 through `fill.to`. Days before counting started stay
 * `undefined`, so the chart draws a gap rather than a zero the Worker never
 * measured.
 */
export function chartRows(daily: DailyRow[], fill?: ChartFill): ChartRow[] {
  const byDay = new Map<string, ChartRow>();
  if (fill?.from) {
    for (const day of daysBetween(fill.from, fill.to)) {
      const entry: ChartRow = { day };
      for (const v of fill.variants) entry[`visitors_${v}`] = 0;
      byDay.set(day, entry);
    }
  }
  for (const row of daily) {
    const entry = byDay.get(row.day) ?? { day: row.day };
    entry[`visitors_${row.variant}`] = row.new_visitors;
    if (row.signups > 0) entry[`signups_${row.variant}`] = row.signups;
    byDay.set(row.day, entry);
  }
  return [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
}

export function variantsOf(detail: { variants: VariantStats[]; daily: DailyRow[] }): string[] {
  const seen = new Set<string>([...detail.variants.map((v) => v.variant), ...detail.daily.map((d) => d.variant)]);
  return [...seen].sort();
}

const LANGUAGE = new Intl.DisplayNames(["en"], { type: "language" });

/** Storefront codes that are not ISO language codes. */
const LANGUAGE_CODES: Record<string, string> = { se: "sv" };

export function languageName(locale: string): string {
  try {
    return LANGUAGE.of(LANGUAGE_CODES[locale] ?? locale) ?? locale;
  } catch {
    return locale;
  }
}

/** `precision-dosing` -> `Precision dosing`: the folder name, read as words. */
export function funnelName(slug: string): string {
  const words = slug.replace(/-/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Glyph + word, never colour alone. */
export function pageStatusLabel(page: Pick<PageSummary, "status" | "live"> & { pending?: boolean }): string {
  if (page.pending) return "◐ Awaiting Damien";
  if (page.status === "retired") return "– Retired";
  if (page.live) return "● Live";
  return "○ Draft";
}

/** A count the Worker could not read is a dash, never 0. */
export function formatSignups(value: number | null): string {
  return value === null ? "–" : formatCount(value);
}

export type FunnelRow = {
  funnel_slug: string;
  live_pages: number;
  pages: number;
  views: number | null;
  visitors: number | null;
  /** Taps on an app-entry page's button: the action such a page asks for, in place of a sign-up. */
  clicks: number | null;
  signups: number | null;
  conversion_pct: number | null;
};

/** The storefront URL that shows exactly this version. A `?v=` view is never counted as a visit. */
export const STOREFRONT_ORIGIN = "https://ketosource.co";

export function pageViewUrl(page: Pick<PageSummary, "page_path" | "variant">): string {
  return `${STOREFRONT_ORIGIN}${page.page_path}?v=${encodeURIComponent(page.variant)}`;
}

/** A figure the Worker did not send (older API) or could not read is a dash. */
export function formatFigure(value: number | null | undefined): string {
  return value == null ? "–" : formatCount(value);
}

export function funnelRows(
  funnels: { funnel_slug: string; totals?: FunnelTotals; pages: PageSummary[] }[],
): FunnelRow[] {
  return funnels.map((funnel) => {
    const counted = funnel.pages.map((p) => p.signups);
    return {
      funnel_slug: funnel.funnel_slug,
      live_pages: funnel.pages.filter((p) => p.live && p.status !== "retired").length,
      pages: funnel.pages.filter((p) => p.status !== "retired").length,
      views: funnel.totals?.views ?? null,
      visitors: funnel.totals?.visitors ?? null,
      clicks: funnel.totals?.clicks ?? null,
      // Older API without totals: fall back to the per-page sum it always showed.
      signups: funnel.totals
        ? funnel.totals.signups
        : counted.some((n) => n === null)
          ? null
          : counted.reduce<number>((sum, n) => sum + (n ?? 0), 0),
      conversion_pct: funnel.totals?.conversion_pct ?? null,
    };
  });
}

/** Language chips for S7: each language once, with its sign-ups, in page order. */
export function languageChips(pages: PageSummary[]): { locale: string; signups: number | null }[] {
  const chips = new Map<string, number | null>();
  for (const page of pages) {
    if (page.status === "retired") continue;
    const prior = chips.has(page.locale) ? chips.get(page.locale)! : 0;
    chips.set(page.locale, prior === null || page.signups === null ? null : prior + page.signups);
  }
  return [...chips.entries()].map(([locale, signups]) => ({ locale, signups }));
}

/** One chip per split-test version (A, B, C…) with how many LIVE pages run it, so gaps in
 *  language parity show at a glance (Damien, 2026-09-28: "the goal is parity"). */
export function versionChips(pages: PageSummary[]): { variant: string; pages: number }[] {
  const chips = new Map<string, number>();
  for (const page of pages) {
    if (page.status === "retired") continue;
    chips.set(page.variant, (chips.get(page.variant) ?? 0) + (page.live ? 1 : 0));
  }
  return [...chips.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([variant, count]) => ({ variant, pages: count }));
}

/** The S7 summary sentence. Totals are the Worker's; the portal only counts pages. */
export function funnelSummaryLine(pages: PageSummary[], days: number, totals?: FunnelTotals): string {
  const current = pages.filter((p) => p.status !== "retired");
  const live = current.filter((p) => p.live).length;
  const livePart = `${live} live ${live === 1 ? "page" : "pages"}`;
  if (totals) {
    if (totals.signups === null) return `Sign-up counts are unavailable right now; ${livePart} still listed.`;
    const signups = `${formatCount(totals.signups)} ${totals.signups === 1 ? "sign-up" : "sign-ups"}`;
    const visitors = totals.visitors === null
      ? "visitors not counted"
      : `${formatCount(totals.visitors)} ${totals.visitors === 1 ? "visitor" : "visitors"}`;
    // Taps are named only where a page asks for one (app-entry), so a sign-up funnel's sentence is unchanged.
    const taps = totals.clicks ? ` ${formatCount(totals.clicks)} ${totals.clicks === 1 ? "tap" : "taps"} on the app button.` : "";
    return `${signups} and ${visitors} in ${days} days across ${livePart}. Conversion ${formatPct(totals.conversion_pct)}.${taps}`;
  }
  if (current.some((p) => p.signups === null)) {
    return `Sign-up counts are unavailable right now; ${livePart} still listed.`;
  }
  const total = current.reduce((sum, p) => sum + (p.signups ?? 0), 0);
  return `${formatCount(total)} ${total === 1 ? "sign-up" : "sign-ups"} in ${days} days across ${livePart}.`;
}

/**
 * A launch moment as the storefront's calendar day. `launch_at` is UTC and a
 * midnight UK launch is 23:00Z the day before, so a UTC slice reads a day early.
 */
export function formatLaunch(iso: string): string {
  if (!iso) return "–";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "Europe/London" });
}

/** The S8 status line: what this page is doing, in words. */
export function pageStatusLine(page: PageDetailData["page"]): string {
  if (page.status === "retired") return `${pageStatusLabel(page)} · ${page.page_path}`;
  if (page.live) {
    const approved = page.approval ? ` · approved by ${personName(page.approval.by)}, ${formatDay(page.approval.at)}` : "";
    return `${pageStatusLabel(page)} · ${page.page_path}${approved}`;
  }
  return `${pageStatusLabel(page)} · launches ${formatLaunch(page.launch_at)}`;
}

/** `damien@ketosource.co` or `damien` -> `Damien`. */
export function personName(by: string): string {
  return titleCase(by.split("@")[0]);
}

function titleCase(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** A sibling link label: `de`, `de B`, `fr (draft)`. */
export function siblingLabel(sibling: { locale: string; variant: string; live: boolean }): string {
  const version = sibling.variant === "a" ? "" : ` ${sibling.variant.toUpperCase()}`;
  return `${sibling.locale}${version}${sibling.live ? "" : " (draft)"}`;
}

/** The S8 Leads sentence. `null` when there is nothing to count yet or counts are unavailable. */
export function leadsLine(leads: PageLeads): string | null {
  if (!leads.counts_available) return null;
  const retrying = leads.pending + leads.failed;
  const parts = [
    `${formatCount(leads.total)} captured`,
    `${formatCount(leads.in_drip)} in Drip`,
    `${formatCount(retrying)} retrying`,
  ];
  const line = parts.join(" · ");
  return leads.stuck > 0 ? `${line}. ${formatCount(leads.stuck)} stopped after six tries and need a look.` : line;
}

/** The approve dialog's sentence: what going live does to the page's versions. */
export function effectLine(effect: { kind: string; live_before: string[]; live_after: string[] }, changes: number): string {
  const letters = (list: string[]) => list.map((v) => v.toUpperCase()).join(", ");
  if (effect.kind === "retire") {
    const gone = effect.live_before.filter((v) => !effect.live_after.includes(v));
    return `Approving retires version ${letters(gone)}. Its visitors go to ${letters(effect.live_after)}.`;
  }
  if (effect.kind === "recover") {
    const back = effect.live_after.filter((v) => !effect.live_before.includes(v));
    return effect.live_after.length > 1
      ? `Approving recovers version ${letters(back)}. Visitors split across ${letters(effect.live_after)}.`
      : `Approving recovers version ${letters(back)}. It gets every visitor to this page.`;
  }
  if (effect.kind === "new_version") {
    const added = effect.live_after.filter((v) => !effect.live_before.includes(v));
    return effect.live_after.length > 1
      ? `Approving adds version ${letters(added)}. Traffic will split ${effect.live_after.length} ways: ${letters(effect.live_after)}.`
      : `Approving puts version ${letters(added)} live.`;
  }
  return `Approving replaces ${changes} image${changes === 1 ? "" : "s"} on this page.`;
}

/** One pending change, in words: "New version (from de-precision-dosing) by Marketer, 25 Sep". */
export function changeLine(change: { action: string; detail: string; by: string; at: string }): string {
  const what =
    change.action === "new_version"
      ? "New version"
      : change.action === "retire"
        ? "Retire"
        : change.action === "recover"
          ? "Recover"
          : "Replace image";
  const detail = change.detail ? ` (${change.detail.replace("->", "→")})` : "";
  return `${what}${detail} by ${personName(change.by)}, ${formatDay(change.at.slice(0, 10))}`;
}

/** The approve dialog's title, body fallback and button, named by what approving does. */
export function approveWording(kind: string | null | undefined): { title: string; fallback: string; button: string } {
  if (kind === "retire") {
    return {
      title: "Approve retiring this version?",
      fallback: "Approving retires this version. It stops getting traffic.",
      button: "Approve and retire",
    };
  }
  if (kind === "recover") {
    return {
      title: "Approve recovering this version?",
      fallback: "Approving puts this version back live. It starts getting traffic again.",
      button: "Approve and recover",
    };
  }
  return { title: "Approve this change?", fallback: "Approving publishes the change.", button: "Approve and publish" };
}

/**
 * Where an approved change is (bug 3fd0402f, recurrence 2: "Live" used to show when only staging had
 * it, and an approval waiting on checks looked like a hang):
 * checking → checks_failed | deploying → staging (a release candidate, live once promoted) → done.
 */
export type ApprovedState = "checking" | "checks_failed" | "deploying" | "staging" | "promoting" | "promote_failed" | "failed" | "done";

/** The status after approval, while the site updates and once it has. `name` is e.g. "Version B". */
export function approvedStatus(
  kind: string,
  state: ApprovedState,
  name: string,
  release?: string | null,
  /**
   * serverFinish: the Worker's own word (site PR 320). true: the console finisher completes the approval server-side,
   * so the tab may close. false: it will not (a commit on the change was not made by the console, or the finisher's
   * one re-run is used up). Absent: an older Worker, where only this open page finishes the publish. rolledBack: the
   * ledger says the release was rolled back (else the promotion simply did not complete). The staging copy promises
   * no automatic promotion: the panel says "going live" only once a Promote run exists.
   */
  detail: {
    serverFinish?: boolean;
    /** finishBlocked(): why the finisher will not complete it (WriteActions). */
    blocked?: "foreign_commit" | "retry_used" | null;
    rolledBack?: boolean;
    /** The promotion completed, then the production check failed (ledger `failed`). */
    productionCheckFailed?: boolean;
    autoMerge?: boolean;
  } = {},
): { title: string; line: string } {
  if (state === "checking") {
    const after = detail.serverFinish === true || detail.autoMerge
      ? "then the change publishes by itself. You can close this page."
      : detail.blocked === "foreign_commit"
        ? "but this change cannot publish from the console: it carries a commit the console did not make. An engineer needs to look at the pull request."
        : "then the change publishes. Keep this page open: it publishes as soon as the checks pass.";
    return { title: "Approved · checking", line: `Your approval is saved. GitHub runs its automatic checks first (about 3 minutes), ${after}` };
  }
  if (state === "checks_failed") {
    if (detail.blocked === "foreign_commit") {
      return {
        title: "Approved · a check failed",
        line: "Your approval is saved, but an automatic check failed and this change carries a commit the console did not make, so it cannot publish from the console. An engineer needs to look at the pull request.",
      };
    }
    if (detail.blocked === "retry_used") {
      return {
        title: "Approved · a check failed",
        line: "Your approval is saved, but an automatic check failed again after its one automatic re-run, so nothing is published yet. Try again runs it once more. If it keeps failing, an engineer needs to look at the check log.",
      };
    }
    return {
      title: "Approved · a check failed",
      line: detail.serverFinish
        ? "Your approval is saved, but an automatic check failed, so nothing is published yet. It is re-run once by itself. If that fails too, this page offers Try again."
        : "Your approval is saved, but an automatic check failed, so nothing is published yet. Try again runs the checks again. If they keep failing, an engineer needs to look at the check log.",
    };
  }
  if (state === "staging") {
    const which = release ? `release ${release}` : "the next release";
    if (kind === "retire") {
      return { title: "Approved · retiring, waiting for promotion", line: `${name} is off staging in ${which}. It leaves the live site when that release is promoted.` };
    }
    if (kind === "recover") {
      return { title: "Approved · recovering, waiting for promotion", line: `${name} is back on staging in ${which}. It returns to the live site when that release is promoted.` };
    }
    return { title: "Approved · waiting for promotion", line: `${name} is merged and on staging in ${which}. It goes live for visitors when that release is promoted.` };
  }
  if (state === "promoting") {
    const which = release ? `Release ${release}` : "The release";
    if (kind === "retire") return { title: "Approved · leaving the live site", line: `${which} is being promoted now (about 5 minutes). ${name} leaves the live site with it.` };
    if (kind === "recover") return { title: "Approved · returning to the live site", line: `${which} is being promoted now (about 5 minutes). ${name} returns to the live site with it.` };
    return { title: "Approved · going live", line: `${which} is being promoted now (about 5 minutes). ${name} goes live for visitors with it.` };
  }
  if (state === "promote_failed") {
    const which = release ? `release ${release}` : "its release";
    const what = detail.rolledBack
      ? `the promotion of ${which} failed its production check and was rolled back`
      : detail.productionCheckFailed
        ? `${which} failed its production check after the promotion`
        : `the promotion of ${which} did not complete`;
    const effect = kind === "retire" ? `${name} is still on the live site` : kind === "recover" ? `${name} is still off the live site` : "visitors still see the old site";
    const verb = kind === "retire" ? "retiring" : kind === "recover" ? "recovering" : "publishing";
    return {
      title: `Approved · ${verb} failed, live site unchanged`,
      line: `The change is merged, but ${what}, so ${effect}. An engineer needs to look at the promotion.`,
    };
  }
  if (kind === "retire") {
    if (state === "deploying") return { title: "Approved · retiring", line: `${name} is being taken off the site. This takes about two minutes.` };
    if (state === "failed") {
      return { title: "Retire failed", line: `${name} is approved to retire but the site did not update. An engineer needs to look at the deploy.` };
    }
    return { title: "Retired", line: `${name} is off the site. Its visitors now go to the remaining live versions.` };
  }
  if (kind === "recover") {
    if (state === "deploying") return { title: "Approved · recovering", line: `${name} is going back on the site. This takes about two minutes.` };
    if (state === "failed") {
      return { title: "Recover failed", line: `${name} is approved to recover but the site did not update. An engineer needs to look at the deploy.` };
    }
    return { title: "Recovered", line: `${name} is back on the site and getting traffic again.` };
  }
  if (state === "deploying") return { title: "Approved · publishing", line: "The change is approved and the site is updating. This takes about two minutes." };
  if (state === "failed") return { title: "Publishing failed", line: "The change is approved but the site did not update. An engineer needs to look at the deploy." };
  return { title: "Live", line: "The change is live." };
}

// ---- Bulk mode (transparency specs/2026-09-29-landing-console-bulk.md) ----

/** One line naming what a batch does, e.g. "New version C in 9 languages". */
export function batchTitle(batch: { kind: string; variants: string[]; items: unknown[] }): string {
  const letters = batch.variants.map((v) => v.toUpperCase()).join(", ");
  const version = `version${batch.variants.length === 1 ? "" : "s"} ${letters}`;
  const where = `in ${batch.items.length} language${batch.items.length === 1 ? "" : "s"}`;
  if (batch.kind === "new_version") return `New ${version} ${where}`;
  if (batch.kind === "retire") return `Retire ${version} ${where}`;
  if (batch.kind === "recover") return `Recover ${version} ${where}`;
  if (batch.kind === "assets") return `Image changes to ${version} ${where}`;
  return `Changes to ${version} ${where}`;
}

/** What approving the whole batch does to traffic, in one sentence. */
export function batchEffectLine(batch: { kind: string; variants: string[]; items: unknown[] }): string {
  const letters = batch.variants.map((v) => v.toUpperCase()).join(", ");
  const n = batch.items.length;
  const languages = `${n} language${n === 1 ? "" : "s"}`;
  if (batch.kind === "new_version") return `Approving puts version ${letters} live in ${languages}. It joins the split test in each.`;
  if (batch.kind === "retire") return `Approving retires version ${letters} in ${languages}. Its visitors go to the other live versions.`;
  if (batch.kind === "recover") return `Approving recovers version ${letters} in ${languages}. It rejoins the split test in each.`;
  return `Approving publishes every change listed here, in ${languages}.`;
}

/** The confirm button for Approve all: names what approving does, so a retire or recover never reads "publish". */
export function batchConfirmLabel(batch: { kind: string }): string {
  if (batch.kind === "retire") return "Approve all and retire";
  if (batch.kind === "recover") return "Approve all and recover";
  return "Approve all and publish";
}

/** The state suffix on a decided batch's title, e.g. " · recovering". Retire and recover never read "publishing". */
export function batchDeployState(kind: string, state: "deploying" | "failed" | "done" | "staging" | "promoting" | "promote_failed"): string {
  if (state === "staging") return kind === "retire" || kind === "recover" ? " · on staging, takes effect once the release is promoted" : " · on staging, live once the release is promoted";
  // Bug 3fd0402f rec 3: the console finisher promotes the release by itself; the batch follows that run too.
  if (state === "promoting") return kind === "retire" || kind === "recover" ? " · release being promoted, takes effect in about 5 minutes" : " · release being promoted, live in about 5 minutes";
  if (state === "promote_failed") return " · promotion failed, live site unchanged";
  if (kind === "retire") return state === "deploying" ? " · retiring" : state === "done" ? " · retired" : " · retire failed";
  if (kind === "recover") return state === "deploying" ? " · recovering" : state === "done" ? " · recovered" : " · recover failed";
  return state === "deploying" ? " · publishing" : state === "done" ? " · live" : " · publishing failed";
}

/**
 * Which languages a bulk retire of `variant` would take down, and which it would refuse because the
 * variant is that language's only live version. The Worker decides; this only previews its rule.
 */
export function retireVariantPlan(
  pages: { locale: string; variant: string; live: boolean; folder: string }[],
  variant: string,
): { retire: string[]; refused: string[] } {
  const retire: string[] = [];
  const refused: string[] = [];
  for (const page of pages) {
    if (page.variant !== variant || !page.live) continue;
    const others = pages.filter((p) => p.locale === page.locale && p.live && p.folder !== page.folder);
    (others.length > 0 ? retire : refused).push(page.locale);
  }
  return { retire: retire.sort(), refused: refused.sort() };
}

/** The versions a bulk retire can offer: live somewhere in the funnel. */
export function retirableVariants(pages: { variant: string; live: boolean }[]): string[] {
  return [...new Set(pages.filter((p) => p.live).map((p) => p.variant))].sort();
}

/**
 * Which languages a bulk recover of `variant` would bring back (retired there), and which the Worker
 * would refuse because that page already has a change waiting. The Worker decides; this only previews.
 */
export function recoverVariantPlan(
  pages: { locale: string; variant: string; status: string; pending?: boolean }[],
  variant: string,
): { recover: string[]; refused: string[] } {
  const recover: string[] = [];
  const refused: string[] = [];
  for (const page of pages) {
    if (page.variant !== variant || page.status !== "retired") continue;
    (page.pending ? refused : recover).push(page.locale);
  }
  return { recover: recover.sort(), refused: refused.sort() };
}

/** The versions a bulk recover can offer: retired in at least one language. */
export function recoverableVariants(pages: { variant: string; status: string }[]): string[] {
  return [...new Set(pages.filter((p) => p.status === "retired").map((p) => p.variant))].sort();
}

/**
 * What the like-for-like cards left out, in one sentence; `null` when nothing was. The language and
 * source chips still count every visitor, so the line says so.
 */
export function comparisonLine(comparison: VariantComparison | undefined): string | null {
  if (!comparison || comparison.basis !== "like_for_like") return null;
  const { new_visitors: visitors, signups } = comparison.left_out;
  if (visitors === 0 && signups === 0) return null;
  const parts: string[] = [];
  const without = comparison.languages_without;
  if (without.length > 0) {
    const missing = [...new Set(without.flatMap((l) => l.missing))].sort();
    const languages = `${formatCount(without.length)} ${without.length === 1 ? "language" : "languages"}`;
    const names = without.map((l) => languageName(l.locale)).join(", ");
    parts.push(
      missing.length === 1
        ? `${languages} without version ${missing[0].toUpperCase()} (${names})`
        : `${languages} missing a version (${names})`,
    );
  }
  const late = comparison.late_languages;
  if (late.length > 0) {
    const languages = `${formatCount(late.length)} ${late.length === 1 ? "language" : "languages"}`;
    parts.push(`the days before every version was live in ${languages}`);
  }
  const what = parts.length > 0 ? parts.join(" and ") : "visits no version could be compared on";
  const counts = `${formatCount(visitors)} ${visitors === 1 ? "visitor" : "visitors"}, ${formatCount(signups)} ${signups === 1 ? "sign-up" : "sign-ups"}`;
  return `Compared like for like: left out ${what} (${counts}). The source and language counts above still include them.`;
}
